import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { SectionNav } from "@/components/section-nav";

type Editorial = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  author: string | null;
  body_html: string | null;
  cover_image_url: string | null;
  published_at: string | null;
};

const fetchEditorial = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row, error } = await supa
      .from("editoriales")
      .select("id,slug,title,subtitle,author,body_html,cover_image_url,published_at")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw error;
    if (!row) throw notFound();
    return row as Editorial;
  });

export const Route = createFileRoute("/comunicaciones/editoriales/$slug")({
  loader: ({ params }) => fetchEditorial({ data: { slug: params.slug } }),
  head: ({ loaderData }) => {
    const e = loaderData;
    const title = e?.title ?? "Editorial | Mine Class";
    const desc = e?.subtitle ?? "Editorial de Mine Class.";
    return {
      meta: [
        { title: `${title} | Mine Class` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        ...(e?.cover_image_url?.startsWith("https://")
          ? [
              { property: "og:image", content: e.cover_image_url },
              { name: "twitter:image", content: e.cover_image_url },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  errorComponent: () => (
    <Shell>
      <p className="text-center py-16 text-[var(--color-on-surface-variant)]">No pudimos cargar esta editorial.</p>
    </Shell>
  ),
  notFoundComponent: () => (
    <Shell>
      <p className="text-center py-16 text-[var(--color-on-surface-variant)]">Editorial no encontrada.</p>
    </Shell>
  ),
  component: Page,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SiteNav />
      <SectionNav section="comunicaciones" />
      <main className="flex-1 w-full">{children}</main>
      <SiteFooter />
    </div>
  );
}

function Page() {
  const e = Route.useLoaderData() as Editorial;
  return (
    <Shell>
      <div className="max-w-container-max mx-auto px-margin-desktop pt-6 w-full">
        <Link to="/comunicaciones/editoriales" className="text-sm underline text-[var(--color-brand-purple)]">
          ← Volver a Editoriales
        </Link>
      </div>
      {e.body_html ? (
        <div className="mc-editorial" dangerouslySetInnerHTML={{ __html: e.body_html }} />
      ) : (
        <div className="max-w-container-max mx-auto px-margin-desktop py-xl w-full">
          <h1 className="text-3xl font-bold text-[var(--color-brand-purple)]">{e.title}</h1>
          {e.subtitle && <p className="mt-2 text-[var(--color-on-surface-variant)]">{e.subtitle}</p>}
        </div>
      )}
    </Shell>
  );
}
